import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Button, Select, Input, InputNumber, Divider } from 'antd';
import { SimpleTable } from '@/components/simple-table';
import RecipientSourceNote from '@/components/team-assignments/RecipientSourceNote';
import { useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import {
  PAYMENT_SLIP_TYPES,
  getPaymentSlipLabelKey,
} from '@/shared/paymentSlips/constants';
import { formatPaymentAmount } from '@/utils/paymentSlipHelpers';
import { PaymentSlipsColumns } from '../table/PaymentSlipsColumns';

/**
 * Company Payment Instructions Form Component
 *
 * Manages payment slips (CRUD operations) and email receivers for a company.
 * Integrates with CompanyGeneralDataForm through onSubmit callback.
 *
 * @param {Object} initialValues - Initial form data (paymentSlips, paymentSlipsEmailReceivers)
 * @param {Function} onSubmit - Callback to parent component with updated data
 * @param {string} className - Additional CSS classes
 * @param {boolean} [readOnly] - Shows the slips and receivers without any
 *   way to change them (someone who may read the company, not edit it)
 */
export default function CompanyPaymentInstructionsForm({
  initialValues,
  companyId,
  className,
  readOnly = false,
}) {
  const { t } = useTranslation(['forms', 'messages', 'tables']);
  const notification = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  // Local state for payment slips and email receivers
  const [paymentSlips, setPaymentSlips] = useState(
    initialValues?.paymentSlips || []
  );
  const [emailReceivers, setEmailReceivers] = useState(
    initialValues?.paymentSlipsEmailReceivers || []
  );
  const [editingIndex, setEditingIndex] = useState(null);

  // Sync state with initialValues when they change (after refetch)
  useEffect(() => {
    setPaymentSlips(initialValues?.paymentSlips || []);
    setEmailReceivers(initialValues?.paymentSlipsEmailReceivers || []);
  }, [initialValues]);

  // Available payment slip types (filter out already created ones)
  const availableTypes = PAYMENT_SLIP_TYPES.filter((type) => {
    // If editing, allow the current type
    if (editingIndex !== null) {
      const editingSlip = paymentSlips[editingIndex];
      if (editingSlip?.typeOfPaymentSlip === type.key) {
        return true;
      }
    }
    // Filter out already created types
    return !paymentSlips.some((slip) => slip.typeOfPaymentSlip === type.key);
  });

  /**
   * Handle form submission (Create or Update)
   */
  const onFinish = (values) => {
    // Format amount with comma and 2 decimals
    const formattedAmount = formatPaymentAmount(values.amountForPayment);

    // Build bank account from nested form fields
    const bankAccount = values.paymentSlip?.recipientAccount;
    const recipientAccount = bankAccount
      ? `${bankAccount.part1 || ''}${(bankAccount.part2 || '').padStart(13, '0')}${bankAccount.part3 || ''}`
      : '';

    const formattedValues = {
      typeOfPaymentSlip: values.typeOfPaymentSlip,
      typeOfPaymentSlipForDisplay: t(
        getPaymentSlipLabelKey(values.typeOfPaymentSlip),
        { ns: 'forms' }
      ),
      dateOfSendingOfPayment: values.dateOfSendingOfPayment,
      purposeOfPayment: values.purposeOfPayment,
      payeeOfPayment: values.payeeOfPayment,
      codeOfPayment: values.codeOfPayment,
      currencyOfPayment: 'rsd',
      amountForPayment: formattedAmount,
      recipientAccount,
      modelNumberApproval: values.modelNumberApproval || '',
      callNumberApproval: values.callNumberApproval || '',
    };

    if (editingIndex !== null) {
      // UPDATE existing payment slip
      const updated = [...paymentSlips];
      updated[editingIndex] = formattedValues;
      setPaymentSlips(updated);
      setEditingIndex(null);
    } else {
      // CREATE new payment slip
      setPaymentSlips([...paymentSlips, formattedValues]);
    }

    // Reset form
    form.resetFields();
  };

  /**
   * Handle edit payment slip
   */
  const handleEdit = (index) => {
    setEditingIndex(index);
    const slip = paymentSlips[index];

    // Parse bank account into parts
    const recipientAccount = slip.recipientAccount || '';
    const bankAccountParts = {
      part1: recipientAccount.substring(0, 3),
      part2: recipientAccount.substring(3, 16),
      part3: recipientAccount.substring(16, 18),
    };

    // Set form values after a short delay to ensure form is rendered
    setTimeout(() => {
      const formValues = {
        typeOfPaymentSlip: slip.typeOfPaymentSlip,
        dateOfSendingOfPayment: slip.dateOfSendingOfPayment,
        purposeOfPayment: slip.purposeOfPayment,
        payeeOfPayment: slip.payeeOfPayment,
        codeOfPayment: slip.codeOfPayment,
        amountForPayment: slip.amountForPayment,
        modelNumberApproval: slip.modelNumberApproval,
        callNumberApproval: slip.callNumberApproval,
        paymentSlip: {
          recipientAccount: bankAccountParts,
        },
      };

      form.setFieldsValue(formValues);

      // Scroll to form
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  /**
   * Handle delete payment slip
   */
  const handleRemove = (index) => {
    setPaymentSlips(paymentSlips.filter((_, i) => i !== index));

    // If editing this slip, cancel edit mode
    if (editingIndex === index) {
      setEditingIndex(null);
      form.resetFields();
    } else if (editingIndex !== null && editingIndex > index) {
      // Adjust editing index if needed
      setEditingIndex(editingIndex - 1);
    }
  };

  /**
   * Handle cancel edit
   */
  const handleCancelEdit = () => {
    setEditingIndex(null);
    form.resetFields();
  };

  /**
   * Handle email receiver changes
   */
  const handleEmailChange = (index, value) => {
    const updated = [...emailReceivers];
    updated[index] = value;
    setEmailReceivers(updated);
  };

  const handleAddEmail = () => {
    setEmailReceivers([...emailReceivers, '']);
  };

  const handleRemoveEmail = (index) => {
    setEmailReceivers(emailReceivers.filter((_, i) => i !== index));
  };

  // Mutation for saving payment slips
  const { mutateAsync: savePaymentSlips, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }
      return await API.companies.updateCompanyById(companyId, payload);
    },
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description:
          response.message || t('paymentSlips.saveSuccess', { ns: 'messages' }),
      });

      // Invalidate company query to trigger refetch
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.company, companyId],
      });
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description:
          error.message || t('paymentSlips.saveError', { ns: 'messages' }),
      });
    },
  });

  /**
   * Handle save payment slips
   */
  const handleSavePaymentSlips = async () => {
    const payload = {
      companyPaymentsSlips: {
        paymentSlips,
        paymentSlipsEmailReceivers: emailReceivers,
      },
    };

    await savePaymentSlips(payload);
  };

  if (readOnly) {
    return (
      <div className={cn('space-y-6 p-4', className)}>
        {paymentSlips.length > 0 ? (
          <div className="rounded-md border bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold">
              {t('paymentSlipsExistingOrders', { ns: 'forms' })}
            </h3>
            <SimpleTable
              data={paymentSlips}
              columns={() => PaymentSlipsColumns({ paymentSlips, t })}
              pagination={false}
            />
          </div>
        ) : null}
        <RecipientSourceNote companyId={companyId} section="PAYMENTS" />
        {emailReceivers.length > 0 && (
          <div className="rounded-md border bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold">
              {t('paymentSlipsEmailReceivers', { ns: 'forms' })}
            </h3>
            <ul className="space-y-1">
              {emailReceivers.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-6 p-4', className)}>
      <RecipientSourceNote companyId={companyId} section="PAYMENTS" />
      {/* Existing Payment Slips List */}
      {paymentSlips.length > 0 && (
        <div className="rounded-md border bg-white p-4">
          <h3 className="mb-4 text-lg font-semibold">
            {t('paymentSlipsExistingOrders', { ns: 'forms' })}
          </h3>
          <SimpleTable
            data={paymentSlips}
            columns={() =>
              PaymentSlipsColumns({ paymentSlips, handleEdit, handleRemove, t })
            }
            pagination={false}
          />
        </div>
      )}

      {/* Email Receivers Section */}
      <div className="rounded-md border bg-white p-4">
        <h3 className="mb-4 text-lg font-semibold">
          {t('paymentSlipsEmailReceivers', { ns: 'forms' })}
        </h3>
        <div className="space-y-2">
          {emailReceivers.map((email, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={email}
                onChange={(e) => handleEmailChange(index, e.target.value)}
                placeholder={t('paymentSlipsEnterEmail', { ns: 'forms' })}
                type="email"
                className="flex-1"
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveEmail(index)}
              >
                {t('remove', { ns: 'forms' })}
              </Button>
            </div>
          ))}
          <Button
            type="dashed"
            onClick={handleAddEmail}
            icon={<PlusOutlined />}
            className="w-full"
          >
            {t('paymentSlipsAddEmail', { ns: 'forms' })}
          </Button>
        </div>
      </div>

      {/* Create/Edit Payment Slip Form */}
      {availableTypes.length > 0 || editingIndex !== null ? (
        <div ref={formRef} className="rounded-md border bg-white p-4">
          <h3 className="mb-4 text-lg font-semibold">
            {editingIndex !== null
              ? t('paymentSlipsEditOrder', { ns: 'forms' })
              : t('paymentSlipsCreateOrder', { ns: 'forms' })}
          </h3>

          <Form form={form} layout="vertical" onFinish={onFinish}>
            {/* Payment Slip Type */}
            <Form.Item
              name="typeOfPaymentSlip"
              label={t('paymentSlipsType', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <Select
                placeholder={t('paymentSlipsSelectType', { ns: 'forms' })}
                disabled={editingIndex !== null}
              >
                {availableTypes.map((type) => (
                  <Select.Option key={type.key} value={type.key}>
                    {t(type.labelKey, { ns: 'forms' })}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Sending Date */}
            <Form.Item
              name="dateOfSendingOfPayment"
              label={t('paymentSlipsSendingDate', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
                {
                  type: 'number',
                  min: 1,
                  max: 31,
                  message: t('paymentSlipsDayValidation', { ns: 'forms' }),
                },
              ]}
            >
              <InputNumber
                placeholder="1-31"
                className="w-full"
                min={1}
                max={31}
              />
            </Form.Item>

            {/* Purpose of Payment */}
            <Form.Item
              name="purposeOfPayment"
              label={t('paymentSlipsPurpose', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
                {
                  max: 35,
                  message: t('paymentSlipsMaxChars', {
                    ns: 'forms',
                    count: 35,
                  }),
                },
              ]}
            >
              <Input.TextArea rows={2} maxLength={35} showCount />
            </Form.Item>

            {/* Payee */}
            <Form.Item
              name="payeeOfPayment"
              label={t('paymentSlipsPayee', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
                {
                  max: 70,
                  message: t('paymentSlipsMaxChars', {
                    ns: 'forms',
                    count: 70,
                  }),
                },
              ]}
            >
              <Input maxLength={70} showCount />
            </Form.Item>

            {/* Payment Code */}
            <Form.Item
              name="codeOfPayment"
              label={t('paymentSlipsCode', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
                {
                  pattern: /^\d{3}$/,
                  message: t('paymentSlipsCode3Digits', { ns: 'forms' }),
                },
              ]}
            >
              <Input maxLength={3} placeholder="XXX" />
            </Form.Item>

            {/* Currency (Disabled) */}
            <Form.Item label={t('paymentSlipsCurrency', { ns: 'forms' })}>
              <Input disabled value="RSD" />
            </Form.Item>

            {/* Amount */}
            <Form.Item
              name="amountForPayment"
              label={t('paymentSlipsAmount', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
                {
                  pattern: /^\d+(,\d{2})?$/,
                  message: t('paymentSlipsAmountFormat', { ns: 'forms' }),
                },
              ]}
            >
              <Input placeholder="123,45" />
            </Form.Item>

            {/* Recipient Account - Using Nested Form Fields */}
            <Form.Item
              label={
                <span>
                  <span className="text-red-500">* </span>
                  {t('paymentSlipsRecipientAccount', { ns: 'forms' })}
                </span>
              }
            >
              <div className="grid grid-cols-[80px_1fr_80px] gap-4">
                <Form.Item
                  name={['paymentSlip', 'recipientAccount', 'part1']}
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t('formValidation.required', { ns: 'forms' }),
                    },
                    {
                      pattern: /^\d{3}$/,
                      message: '3 digits',
                    },
                  ]}
                >
                  <Input maxLength={3} placeholder="XXX" />
                </Form.Item>

                <Form.Item
                  name={['paymentSlip', 'recipientAccount', 'part2']}
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t('formValidation.required', { ns: 'forms' }),
                    },
                    {
                      pattern: /^\d+$/,
                      message: 'Only digits',
                    },
                  ]}
                >
                  <Input maxLength={13} placeholder="XXXXXXXXXXXXX" />
                </Form.Item>

                <Form.Item
                  name={['paymentSlip', 'recipientAccount', 'part3']}
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t('formValidation.required', { ns: 'forms' }),
                    },
                    {
                      pattern: /^\d{2}$/,
                      message: '2 digits',
                    },
                  ]}
                >
                  <Input maxLength={2} placeholder="XX" />
                </Form.Item>
              </div>
            </Form.Item>

            {/* Approval Section */}
            <Divider>{t('paymentSlipsApproval', { ns: 'forms' })}</Divider>

            <Form.Item
              name="modelNumberApproval"
              label={t('paymentSlipsModelNumber', { ns: 'forms' })}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="callNumberApproval"
              label={t('paymentSlipsCallNumber', { ns: 'forms' })}
            >
              <Input />
            </Form.Item>

            {/* Form Actions */}
            <div className="flex justify-end gap-2">
              {editingIndex !== null && (
                <Button className="rounded-full" onClick={handleCancelEdit}>
                  {t('cancel', { ns: 'forms' })}
                </Button>
              )}
              <Button type="primary" className="rounded-full" htmlType="submit">
                {editingIndex !== null
                  ? t('save', { ns: 'forms' })
                  : t('create', { ns: 'forms' })}
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div className="rounded-md border bg-gray-50 p-8 text-center text-gray-500">
          {t('paymentSlipsAllTypesCreated', { ns: 'forms' })}
        </div>
      )}

      {/* Save Button */}
      {companyId && (
        <div className="flex justify-end">
          <Button
            type="primary"
            size="large"
            className="rounded-full"
            onClick={handleSavePaymentSlips}
            loading={isPending}
            disabled={isPending}
          >
            {t('saveChanges', { ns: 'forms' })}
          </Button>
        </div>
      )}
    </div>
  );
}
